import React, { ReactElement, ReactNode } from "react";

// Centred status card from the approved CMP Offline + Error Pages canvas
// (Moeen, Oct 27 2026): offline screen C and the Style 1 error pages.

export type StatusCardTone = "gold" | "red" | "neutral";

const toneColors: Record<StatusCardTone, { tile: string; icon: string }> = {
  gold: { tile: "#FBF0DC", icon: "#C6912A" },
  red: { tile: "#FBE4E4", icon: "#B42318" },
  neutral: { tile: "#F4F4F5", icon: "#0A0A0A" },
};

export type StatusCardProps = {
  icon: ReactNode;
  tone: StatusCardTone;
  eyebrow: string;
  title: string;
  description: string;
  actions: ReactNode;
  hint?: string;
  isAlert?: boolean;
  wide?: boolean;
};

export const StatusCard = ({
  icon,
  tone,
  eyebrow,
  title,
  description,
  actions,
  hint,
  isAlert = false,
  wide = false,
}: StatusCardProps): ReactElement => (
  <section
    role={isAlert ? "alert" : undefined}
    className={`flex w-full ${wide ? "max-w-[560px]" : "max-w-[520px]"} flex-col items-center gap-4 rounded-xl border border-[#E4E4E7] bg-white px-6 pb-9 pt-10 text-center text-black shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:px-10`}
  >
    <div
      aria-hidden="true"
      className="flex h-16 w-16 items-center justify-center rounded-xl [&_svg]:!text-[32px]"
      style={{ backgroundColor: toneColors[tone].tile, color: toneColors[tone].icon }}
    >
      {icon}
    </div>
    <span className="text-[12px] uppercase leading-4 tracking-[0.06em] text-[#727272]">
      {eyebrow}
    </span>
    <h1 className="m-0 text-[28px] font-semibold leading-[1.25]">{title}</h1>
    <p className="m-0 text-[14px] leading-[1.55]">{description}</p>
    <div className="mt-3 flex flex-wrap justify-center gap-3">{actions}</div>
    {hint ? (
      <span className="text-[12px] leading-4 text-[#727272]">{hint}</span>
    ) : null}
  </section>
);

export type StatusCardButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "primary" | "outline";
  };

export const StatusCardButton = ({
  variant = "primary",
  className = "",
  children,
  ...props
}: StatusCardButtonProps): ReactElement => (
  <button
    type="button"
    {...props}
    className={`inline-flex items-center justify-center rounded-lg px-5 py-[11px] text-[14px] font-semibold leading-5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cpaGold disabled:cursor-not-allowed disabled:opacity-50 ${
      variant === "primary"
        ? "border border-[#E1A731] bg-[#E1A731] text-white hover:bg-[#C6912A]"
        : "border border-[#9E9E9E] bg-white text-[#0A0A0A] hover:bg-[#F7F7F8]"
    } ${className}`}
  >
    {children}
  </button>
);
